<script setup lang="ts">
// ================================
// HISTORIQUE ET CYCLE DE VIE DES ÉTUDES (DEV-1913, A12, DEV-2150, DEV-2156).
//
// Ce que le client doit vivre : il retrouve les études de son compteur avec leur
// date de calcul lisible, il en rouvre une pour lire ce qui avait été servi, il
// la REJOUE sur les données du jour (une étude neuve, l'ancienne reste lisible et
// marquée « remplacée le … »), il REVIENT à une étude antérieure sans recalcul,
// il en SUPPRIME une après confirmation dans la page.
//
// Lot 4d (contrat de l'interface v1.2 §8.4, DEV-2419) : quatre colonnes dans
// l'ordre de lecture d'un client, « Calculée le » · « Réf. » · « Résultat » ·
// « Actions » ; la colonne « Origine » disparaît (son « Rétablie le … » passe
// sous la date) ; le résultat écrit le montant seul, signé et coloré, et le
// scénario du montant servi (C25) ; en C5, « Risque de disjonction · 11 kVA »
// sans jamais nommer un scénario des dépassements (D18) ; rien à faire sur
// l'étude affichée (8.4.9).
//
// Une ligne = un LANCEMENT : la page calcule deux scénarios à chaque lancement
// (sans et avec dépassement), et les montrer comme deux études identiques
// faisait croire à des doublons (DEV-2156). Chaque geste vise les deux.
//
// DEV-2423 (§8.4.10, §8.4.11, C34) : une seule recommandation en vigueur, les
// précédentes portent « Remplacée le … » même après une relance depuis le
// formulaire ; des relances identiques tiennent sur une ligne, « 4 études
// identiques (13:16 → 13:56) », les gestes visant la plus récente.
// ================================
import { computed, ref } from "vue"
import OptimTurpeV2TexteSigne from "./OptimTurpeV2TexteSigne.vue"
import { useI18n } from "vue-i18n"
import { CYCLE_ROUTES_ACTIVES } from "@modules/courtage/constants/optim-turpe-v2"
import {
    CONSEIL_NON_ETABLI,
    estDeVersionAnterieure,
    estEnAttenteAbandonnee,
    etudeDuResultat,
    grouperParLancement,
    libelleDuRegroupement,
    lignesDHistorique,
    lireCycle,
    lireOrigine,
    remplaceeLeDuLancement,
    relanceDuLancement,
    resultatsDuLancement,
    scenarioServi,
    type LancementLu,
} from "@modules/courtage/utils/optim-turpe-v2.cycle"
import { lireChiffre } from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import { lireEchec } from "@modules/courtage/utils/optim-turpe-v2.echec"
import {
    CODE_RESERVE_C5_PIC,
    reserveC5EnTitre,
} from "@modules/courtage/utils/optim-turpe-v2.recommandation"
import { referenceCourteEtude } from "@modules/courtage/utils/optim-turpe-v2.demande"
import { surLaPeriode } from "@modules/courtage/utils/optim-turpe-v2.titre"
import { formatDateHeureParis } from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    LIBELLE_ETAT,
    LIBELLE_SCENARIO_ARBITRAGE,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import {
    NIVEAU_1,
    NIVEAU_3,
} from "@modules/courtage/utils/optim-turpe-v2.boutons"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            // Revue témoin du 15/10/2026 (D20, Q7) : la liste porte aussi
            // l'étude affichée ; « Relance » était du jargon.
            vide: "Aucune étude sur ce compteur.",
            tronquee:
                "Seules les études les plus récentes sont listées ici : les plus anciennes restent disponibles auprès de l'assistance.",
            // Contrat v1.2 §8.4.2.
            colonnes: {
                date: "Calculée le",
                reference: "Réf.",
                etat: "Résultat",
                actions: "Actions",
            },
            retablie: "Rétablie le {date}",
            // §8.4.7 (D9, D18) : jamais « conseillés », jamais un scénario.
            risqueC5: "Risque de disjonction",
            remplacee: "Remplacée le {date}",
            // DEV-2311 : une étude de l'ancien moteur reste lisible, mais
            // n'est plus le résultat du compteur.
            versionAnterieure: "Produite par une version antérieure",
            courante: "Étude affichée",
            // D5 : l'état d'une étude saisie, comme sa carte (« Vos puissances »).
            vosPuissances: "Vos puissances",
            etatInconnu: "État non disponible",
            // DEV-2439 : un lancement refusé dit qu'il n'a pas abouti, suivi
            // de la cause courte servie par le moteur (« Aucune mesure
            // disponible sur la période »), jamais « État non disponible ».
            pasAbouti: "N'a pas abouti",
            // S9 (15/10/2026) : le libellé court d'une étude C5 dont la carte
            // porte en titre la réserve « pic au-dessus de la souscrite ».
            enCalcul: "Calcul en cours",
            // DEV-2412 : une étude restée en attente au delà de quinze minutes
            // ne disparaît plus ; elle se relance sur les mêmes réglages.
            enAttenteDepuis: "En attente de calcul depuis {heure}",
            relancer: "Relancer",
            relancerAide: "Relance une étude sur les mêmes réglages.",
            // Finitions après relecture : une étude déjà relancée ne se
            // relance plus tant que sa relance existe.
            relanceeA: "Relancée à {heure}",
            relanceeAEtat: "relancée à {heure}",
            // « Rouvrir » relit ou rétablit une étude passée, sans recalcul ;
            // la relance sur les dernières mesures est « Nouvelle étude », en
            // tête du résultat (Q26).
            ouvrir: "Rouvrir",
            ouvrirAide: "Relit cette étude, sans recalcul.",
            retablir: "Rouvrir",
            retablirAide:
                "Revient à cette étude telle qu'elle avait été calculée, sans recalcul.",
            supprimer: "Supprimer",
            // Revue 2 en production (I90) : la question nomme l'étude visée.
            confirmerTitre: "Supprimer l'étude du {date} ?",
            confirmerTexte:
                "L'étude ne sera plus affichée dans cette liste. Elle reste conservée, et l'assistance peut la retrouver en cas de question sur le montant.",
            annuler: "Annuler",
            confirmer: "Supprimer",
        },
    },
})

type Props = {
    etudes: OptimTurpeV2Etude[]
    /** L'étude actuellement affichée par la page, pour la marquer dans la liste. */
    etudeCouranteId?: string | null
    /** La suppression est-elle en cours ? La ligne ne se clique pas deux fois. */
    suppressionEnCours?: boolean
    /**
     * Le geste du cycle en cours et les études qu'il vise (état de chargement).
     * « rejouer » y reste : la relance du pied du cadre et « Nouvelle étude »
     * le portent, et il désactive les boutons de la liste.
     */
    geste?: { type: "rejouer" | "retablir" | "supprimer"; ids: string[] } | null
    /** L'échec NOMMÉ du dernier geste, dit sous la liste. */
    gesteErreur?: string | null
    /** Une étude est-elle en calcul ? « Rejouer » attend alors la fin. */
    calculEnCours?: boolean
    /** La liste a-t-elle été coupée par sa limite (servi par le moteur) ? */
    tronquee?: boolean | null
}
const props = withDefaults(defineProps<Props>(), {
    etudeCouranteId: null,
    suppressionEnCours: false,
    geste: null,
    gesteErreur: null,
    calculEnCours: false,
    tronquee: null,
})

const emit = defineEmits<{
    ouvrir: [ids: string[]]
    supprimer: [ids: string[]]
    retablir: [ids: string[]]
    /** Relancer une étude restée en attente, sur les mêmes réglages (DEV-2412). */
    relancer: [ids: string[]]
}>()

/** « 06:03 » : l'heure de Paris d'un horodatage, ou `null`. */
function heureParis(iso: string | null | undefined): string | null {
    const ecrit = formatDateHeureParis(iso)
    const heure = ecrit.split(" à ")[1]
    return heure ?? null
}

/** Le lancement à supprimer, en attente de confirmation. `null` = aucune demande. */
const aSupprimer = ref<LancementLu | null>(null)

/** Le geste en cours vise-t-il ce lancement ? */
function gesteSur(lancement: LancementLu, type: string): boolean {
    return (
        props.geste?.type === type &&
        props.geste.ids.some((id) => lancement.ids.includes(id))
    )
}

/** Le résultat d'une ligne (§8.4.5 à §8.4.7), en trois morceaux. */
type ResultatDeLaLigne = {
    /** « Rien à changer sur l'acheminement », « Vos puissances : »… */
    libelle: string | null
    /** Le montant seul, signé et coloré (« +1 161 € »). */
    montant: string | null
    /** Ce qui suit le montant : « HT par an · Assumer les dépassements ». */
    suite: string | null
}

/** « 11 kVA » : la puissance qui lève le risque en C5, servie par la réserve. */
function puissanceAdapteeC5(etude: OptimTurpeV2Etude): string | null {
    const reserve = (etude.reserves ?? []).find(
        (r) => r?.code === CODE_RESERVE_C5_PIC
    )
    const valeur = (reserve?.valeurs as Record<string, unknown> | undefined)
        ?.puissance_adaptee_kva
    return typeof valeur === "number" && Number.isFinite(valeur)
        ? `${String(valeur).replace(".", ",")}\u00a0kVA`
        : null
}

/** Le scénario du montant servi (C25), jamais en C5 (D18). */
function scenarioDuMontant(
    lancement: LancementLu,
    etude: OptimTurpeV2Etude
): string | null {
    if (etude.compteur?.segment === "C5") return null
    if (lancement.sans && lancement.avec) {
        return etude === lancement.avec
            ? LIBELLE_SCENARIO_ARBITRAGE.avec
            : LIBELLE_SCENARIO_ARBITRAGE.sans
    }
    const servi = scenarioServi(etude)
    if (servi === "avec_depassements") return LIBELLE_SCENARIO_ARBITRAGE.avec
    if (servi === "sans_depassements") return LIBELLE_SCENARIO_ARBITRAGE.sans
    return null
}

/**
 * Le résultat de la ligne, lu sur la MÊME étude que son montant
 * (`etudeDuResultat`, revue de nuit du 15/10/2026, E1). Une paire sans
 * conseil établi écrit « conseil non établi » (D5 du 15/10/2026).
 */
function resultatDeLaLigne(
    lancement: LancementLu,
    terminee: boolean,
    resultats: string | null,
    enAttenteAbandonnee: boolean,
    relanceeA: string | null
): ResultatDeLaLigne {
    const seul = (libelle: string | null): ResultatDeLaLigne => ({
        libelle,
        montant: null,
        suite: null,
    })
    if (enAttenteAbandonnee) {
        const heure = heureParis(lancement.principale.created_at)
        if (heure) {
            const attente = t("enAttenteDepuis", { heure })
            return seul(
                relanceeA
                    ? `${attente}, ${t("relanceeAEtat", { heure: relanceeA })}`
                    : attente
            )
        }
    }
    if (!terminee) return seul(t("enCalcul"))
    const refus = refusDuLancement(lancement)
    if (refus) {
        const cause = causeCourteDuRefus(refus)
        return seul(cause ? `${t("pasAbouti")} · ${cause}` : t("pasAbouti"))
    }
    const etude = etudeDuResultat(lancement)
    if (!etude) {
        if (resultats === CONSEIL_NON_ETABLI) return seul(CONSEIL_NON_ETABLI)
        // Paire sans montant (échecs) : l'état de l'étude principale.
        const etat = lireChiffre(lancement.principale).etat
        return seul(etat ? LIBELLE_ETAT[etat] : t("etatInconnu"))
    }
    const chiffre = lireChiffre(etude)
    const unite = `HT ${surLaPeriode(chiffre.periode.jours, chiffre.periode)}`
    // D5 : une étude saisie se nomme comme sa carte, « Vos puissances ».
    if (chiffre.estScenarioLibre) {
        return resultats
            ? {
                  libelle: `${t("vosPuissances")} :`,
                  montant: resultats,
                  suite: unite,
              }
            : seul(t("vosPuissances"))
    }
    // §8.4.7 : la réserve C5 portée en titre de la carte.
    if (reserveC5EnTitre(etude)) {
        const puissance = puissanceAdapteeC5(etude)
        return seul(
            puissance ? `${t("risqueC5")} · ${puissance}` : t("risqueC5")
        )
    }
    const etat = chiffre.etat
    // §8.4.6 : « Rien à changer » et « Économie non chiffrée » se disent en
    // un mot, comme la carte.
    const sansMontant =
        etat === "deja_optimale" ||
        etat === "surcout" ||
        etat === "montant_non_servi"
    // §8.4.5 : une économie, le montant seul puis le scénario du montant.
    if (!sansMontant && resultats && resultats !== CONSEIL_NON_ETABLI) {
        const scenario = scenarioDuMontant(lancement, etude)
        return {
            libelle: null,
            montant: resultats,
            suite: scenario ? `${unite} · ${scenario}` : unite,
        }
    }
    return seul(etat ? LIBELLE_ETAT[etat] : t("etatInconnu"))
}

/**
 * L'étude refusée d'un lancement qui n'a rien produit (aucune étude aboutie),
 * ou `null`. Un lancement dont un scénario a abouti se lit sur ce scénario.
 */
function refusDuLancement(lancement: LancementLu): OptimTurpeV2Etude | null {
    if (lancement.etudes.some((e) => e.status === "SUCCESS")) return null
    return lancement.etudes.find((e) => e.status === "FAILURE") ?? null
}

/**
 * La cause courte d'un refus : le message SERVI par le moteur (le titre de
 * son catalogue, « Aucune mesure disponible sur la période »), sa première
 * phrase, sans point final. Jamais la phrase longue de la page : elle se lit
 * sur l'étude rouverte. `null` sans message servi.
 */
function causeCourteDuRefus(etude: OptimTurpeV2Etude): string | null {
    const echec = lireEchec(etude)
    if (!echec?.messageServi || !echec.message) return null
    const premiere = echec.message.trim().split(/(?<=[.!?])\s+/)[0] ?? ""
    const courte = premiere.replace(/[.\s]+$/, "")
    return courte === "" ? null : courte
}

/** « 712409 » : la référence courte de l'étude de la ligne (§8.4.4, §8.5). */
function referenceDeLaLigne(lancement: LancementLu): string | null {
    const etude = etudeDuResultat(lancement) ?? lancement.principale
    const id = etude._id ? String(etude._id) : ""
    return id ? referenceCourteEtude(id).replace(/^Réf\.\s*/, "") : null
}

const getLignes = computed(() => {
    const lancements = grouperParLancement(props.etudes)
    const maintenant = Date.now()
    return lignesDHistorique(lancements, props.etudeCouranteId).map((ligne) => {
        const lancement = ligne.lancement
        const etude = lancement.principale
        const cycle = lireCycle(etude)
        const origine = lireOrigine(etude)
        // DEV-2412 : une étude du lancement attend son calcul au delà du
        // seuil ; le lancement se relance sur les mêmes réglages.
        const aRelancer = lancement.etudes.some((e) =>
            estEnAttenteAbandonnee(e, maintenant)
        )
        // Finitions après relecture (réserve 2) : un lancement dont une étude
        // a abouti reste un lancement lisible ; « En attente de calcul » ne
        // nomme que celui qui n'a rien à lire.
        const aboutie = lancement.etudes.some((e) => e.status === "SUCCESS")
        const enAttenteAbandonnee = aRelancer && !aboutie
        const terminee =
            etude.status === "SUCCESS" ||
            etude.status === "FAILURE" ||
            (aRelancer && aboutie)
        // Finitions après relecture (réserve 1) : déjà relancé sur les mêmes
        // réglages, le lancement ne propose plus « Relancer ».
        const relance = aRelancer
            ? relanceDuLancement(lancement, lancements)
            : null
        const relanceeA = relance
            ? heureParis(relance.principale.created_at)
            : null
        // « Rouvrir » vise les seules études qui ont un résultat à relire.
        const idsARouvrir = aRelancer
            ? lancement.etudes
                  .filter(
                      (e) => e.status === "SUCCESS" || e.status === "FAILURE"
                  )
                  .map((e) => String(e._id ?? ""))
                  .filter(Boolean)
            : lancement.ids
        const courante =
            props.etudeCouranteId !== null &&
            lancement.ids.includes(props.etudeCouranteId)
        // Une étude remplacée par un rejeu, ou qui n'est plus la courante de
        // son cycle, se rétablit ; une courante se rejoue. Jamais les deux.
        const remplacee =
            cycle.remplaceePar !== null || cycle.courante === false
        const resultats = terminee ? resultatsDuLancement(lancement) : null
        // §8.4.10 : le champ servi d'abord, sinon la règle de la page.
        const remplaceeLe = remplaceeLeDuLancement(lancement, lancements)
        return {
            lancement,
            cle: lancement.cle,
            reference: referenceDeLaLigne(lancement),
            date: formatDateHeureParis(lancement.date),
            resultat: resultatDeLaLigne(
                lancement,
                terminee,
                resultats,
                enAttenteAbandonnee,
                relanceeA
            ),
            enAttenteAbandonnee,
            aRelancer,
            relanceeA,
            idsARouvrir,
            retablieLe: origine.retablieLe
                ? formatDateHeureParis(origine.retablieLe)
                : null,
            remplaceeLe: remplaceeLe ? formatDateHeureParis(remplaceeLe) : null,
            regroupement: libelleDuRegroupement(ligne, formatDateHeureParis),
            courante,
            versionAnterieure: lancement.etudes.some(estDeVersionAnterieure),
            peutRetablir: CYCLE_ROUTES_ACTIVES && terminee && remplacee,
        }
    })
})

function onDemanderSuppression(lancement: LancementLu): void {
    aSupprimer.value = lancement
}
function onConfirmerSuppression(): void {
    const ids = aSupprimer.value?.ids ?? []
    aSupprimer.value = null
    if (ids.length > 0) emit("supprimer", ids)
}
</script>

<template>
    <!-- Revue 2 en production (I30, I49) : le titre « Études de ce
         compteur » est celui de la rangée qui contient la liste ; la liste ne
         le répète pas. -->
    <section
        class="optim-turpe-v2-historique flex flex-col gap-3"
        data-testid="optim-turpe-v2-historique"
    >
        <div v-if="getLignes.length > 0" class="overflow-x-auto">
            <table class="w-full text-left text-sm sm:min-w-[30rem]">
                <thead class="text-primary-500">
                    <tr>
                        <th class="py-1 pr-3 font-medium">
                            {{ t("colonnes.date") }}
                        </th>
                        <th class="py-1 pr-3 font-medium">
                            {{ t("colonnes.reference") }}
                        </th>
                        <th class="py-1 pr-3 font-medium">
                            {{ t("colonnes.etat") }}
                        </th>
                        <th class="py-1 text-right font-medium">
                            {{ t("colonnes.actions") }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="ligne in getLignes" :key="ligne.cle">
                        <tr
                            class="border-t border-surface-100 align-top"
                            data-testid="optim-turpe-v2-historique-ligne"
                        >
                            <!-- §8.4.3 : la date, puis l'état de la ligne. -->
                            <td class="py-1.5 pr-3 text-primary-800">
                                {{ ligne.date }}
                                <span
                                    v-if="ligne.courante"
                                    class="block text-xs text-primary-500"
                                    >{{ t("courante") }}</span
                                >
                                <!-- §8.4.11 : des relances identiques, une ligne. -->
                                <span
                                    v-if="ligne.regroupement"
                                    class="block text-xs text-primary-500"
                                    data-testid="optim-turpe-v2-historique-regroupement"
                                    >{{ ligne.regroupement }}</span
                                >
                                <!-- Une étude remplacée (rejeu, ou relance depuis le
                                 formulaire, §8.4.10) reste lisible et le dit,
                                 sur sa propre ligne (§7). -->
                                <span
                                    v-if="ligne.remplaceeLe"
                                    class="block text-xs text-primary-400"
                                    data-testid="optim-turpe-v2-historique-remplacee"
                                    >{{
                                        t("remplacee", {
                                            date: ligne.remplaceeLe,
                                        })
                                    }}</span
                                >
                                <span
                                    v-if="ligne.retablieLe"
                                    class="block text-xs text-primary-400"
                                    data-testid="optim-turpe-v2-historique-retablie"
                                    >{{
                                        t("retablie", {
                                            date: ligne.retablieLe,
                                        })
                                    }}</span
                                >
                                <span
                                    v-if="ligne.versionAnterieure"
                                    class="block text-xs text-primary-400"
                                    data-testid="optim-turpe-v2-historique-version-anterieure"
                                    >{{ t("versionAnterieure") }}</span
                                >
                            </td>
                            <!-- §8.4.4 : la référence courte. -->
                            <td
                                class="py-1.5 pr-3 text-primary-800 tabular-nums"
                                data-testid="optim-turpe-v2-historique-reference"
                            >
                                {{ ligne.reference ?? "" }}
                            </td>
                            <!-- §8.4.5 à §8.4.7 : le résultat. -->
                            <td
                                class="py-1.5 pr-3 text-primary-900"
                                data-testid="optim-turpe-v2-historique-resultat"
                            >
                                <template v-if="ligne.resultat.libelle">{{
                                    ligne.resultat.libelle
                                }}</template>
                                <template v-if="ligne.resultat.montant"
                                    >{{ ligne.resultat.libelle ? " " : ""
                                    }}<span
                                        class="font-semibold"
                                        data-testid="optim-turpe-v2-historique-resultats"
                                        ><OptimTurpeV2TexteSigne
                                            :texte="
                                                ligne.resultat.montant
                                            " /></span
                                ></template>
                                <template v-if="ligne.resultat.suite">{{
                                    ` ${ligne.resultat.suite}`
                                }}</template>
                            </td>
                            <td class="py-1.5">
                                <div class="flex flex-wrap justify-end gap-1">
                                    <!-- « Rouvrir une étude » : une étude remplacée se
                                     rétablit, une autre se relit ; un seul verbe.
                                     Revue 2 (I89) : jamais sur l'étude
                                     affichée. -->
                                    <BaseButton
                                        v-if="ligne.aRelancer"
                                        v-bind="NIVEAU_3"
                                        :label="t('relancer')"
                                        :title="
                                            ligne.relanceeA
                                                ? t('relanceeA', {
                                                      heure: ligne.relanceeA,
                                                  })
                                                : t('relancerAide')
                                        "
                                        :disabled="
                                            calculEnCours ||
                                            geste !== null ||
                                            ligne.relanceeA !== null
                                        "
                                        data-testid="optim-turpe-v2-historique-relancer"
                                        @click="
                                            emit(
                                                'relancer',
                                                ligne.lancement.ids
                                            )
                                        "
                                    />
                                    <template
                                        v-if="
                                            !ligne.courante &&
                                            !ligne.enAttenteAbandonnee &&
                                            ligne.idsARouvrir.length > 0
                                        "
                                    >
                                        <BaseButton
                                            v-if="!ligne.peutRetablir"
                                            v-bind="NIVEAU_3"
                                            :label="t('ouvrir')"
                                            :title="t('ouvrirAide')"
                                            data-testid="optim-turpe-v2-historique-ouvrir"
                                            @click="
                                                emit(
                                                    'ouvrir',
                                                    ligne.idsARouvrir
                                                )
                                            "
                                        />
                                        <BaseButton
                                            v-else
                                            v-bind="NIVEAU_3"
                                            :label="t('retablir')"
                                            :title="t('retablirAide')"
                                            :loading="
                                                gesteSur(
                                                    ligne.lancement,
                                                    'retablir'
                                                )
                                            "
                                            :disabled="geste !== null"
                                            data-testid="optim-turpe-v2-historique-retablir"
                                            @click="
                                                emit(
                                                    'retablir',
                                                    ligne.lancement.ids
                                                )
                                            "
                                        />
                                    </template>
                                    <!-- R21 : « Supprimer » en lien discret ;
                                     la confirmation suit, dans la page.
                                     §8.4.9 : jamais sur l'étude affichée. -->
                                    <BaseButton
                                        v-if="!ligne.courante"
                                        :label="t('supprimer')"
                                        size="small"
                                        text
                                        severity="secondary"
                                        data-niveau="3"
                                        class="px-0! font-medium! text-primary-500! underline! underline-offset-2!"
                                        :loading="
                                            gesteSur(
                                                ligne.lancement,
                                                'supprimer'
                                            )
                                        "
                                        :disabled="
                                            suppressionEnCours || geste !== null
                                        "
                                        data-testid="optim-turpe-v2-historique-supprimer"
                                        @click="
                                            onDemanderSuppression(
                                                ligne.lancement
                                            )
                                        "
                                    />
                                </div>
                            </td>
                        </tr>
                        <!-- La confirmation se fait DANS la page, jamais par
                         `confirm()` ; revue 2 (I90) : sous la ligne visée. -->
                        <tr
                            v-if="aSupprimer?.cle === ligne.cle"
                            data-testid="optim-turpe-v2-historique-confirmation-ligne"
                        >
                            <td colspan="4" class="pb-2">
                                <div
                                    class="flex flex-col gap-2 rounded-md border border-[var(--p-red-200)] bg-[var(--p-red-50)] p-3"
                                    role="alertdialog"
                                    data-testid="optim-turpe-v2-historique-confirmation"
                                >
                                    <p
                                        class="text-sm font-medium text-[var(--p-red-900)]"
                                    >
                                        {{
                                            t("confirmerTitre", {
                                                date: ligne.date,
                                            })
                                        }}
                                    </p>
                                    <p class="text-sm text-[var(--p-red-800)]">
                                        {{ t("confirmerTexte") }}
                                    </p>
                                    <div class="flex gap-2">
                                        <BaseButton
                                            v-bind="NIVEAU_3"
                                            :label="t('annuler')"
                                            data-testid="optim-turpe-v2-historique-annuler"
                                            @click="aSupprimer = null"
                                        />
                                        <BaseButton
                                            v-bind="NIVEAU_1"
                                            :label="t('confirmer')"
                                            severity="danger"
                                            data-testid="optim-turpe-v2-historique-confirmer"
                                            @click="onConfirmerSuppression"
                                        />
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <p v-else class="text-xs text-primary-500">{{ t("vide") }}</p>

        <p v-if="tronquee === true" class="text-xs text-primary-500">
            {{ t("tronquee") }}
        </p>

        <!-- L'échec d'un geste, NOMMÉ, sous la liste qu'il concerne. -->
        <BaseMessage
            v-if="gesteErreur"
            severity="warn"
            variant="simple"
            size="small"
            data-testid="optim-turpe-v2-historique-erreur"
        >
            {{ gesteErreur }}
        </BaseMessage>
    </section>
</template>
